"use client";

import "react-international-phone/style.css";

import clsx from "clsx";
import React, { useEffect } from "react";
import {
  CountryData,
  PhoneInput as ReactPhoneInput,
  defaultCountries,
} from "react-international-phone";
import { SupportedCountries } from "@/enums/internationalization";
import { useOrg } from "@/hooks/useOrg";
import { parsePhoneNumber } from "awesome-phonenumber";

interface PhoneInputProps {
  id?: string;
  value: string;
  label?: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  className?: string;
  inputClassName?: string;
  countrySelectorClassName?: string;
  /** Matches the 42px height of the library inputs. */
  compact?: boolean;
  /** Theme overrides for the outer container (background, border color). */
  containerStyle?: React.CSSProperties;
  /** Theme overrides for the inner text input (background, text color). */
  inputStyle?: React.CSSProperties;
  allowedCountries?: SupportedCountries[];
  /**
   * When true, shows all countries in the dropdown instead of filtering
   * by the org's allowedCountries. Useful for features like Do Not Call
   * where any country's phone number should be accepted.
   */
  allowAllCountries?: boolean;
  setValue: (value: string) => void;
  setValid: (isValid: boolean) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  initialCountry?: SupportedCountries;
  /** Accessible name when no visible `label` is rendered. */
  ariaLabel?: string;
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  id,
  initialCountry,
  label,
  placeholder,
  value,
  disabled,
  error = false,
  helperText,
  className,
  inputClassName,
  countrySelectorClassName,
  compact = false,
  containerStyle,
  inputStyle,
  required,
  allowedCountries,
  allowAllCountries = false,
  setValue,
  setValid,
  onKeyDown = () => {},
  onFocus = () => {},
  onBlur = () => {},
  ariaLabel,
}) => {
  const { ui } = useOrg();

  const countriesAllowed = allowedCountries || ui.allowedCountries || [];

  // Internal country state uses a plain string (ISO2 uppercase) so it can
  // represent ANY country when allowAllCountries is enabled, not just those
  // in the SupportedCountries enum.
  const [country, setCountry] = React.useState<string>(
    initialCountry || ui.country || SupportedCountries.UnitedStates
  );

  const isCountryInAllowedList =
    allowAllCountries || countriesAllowed.includes(country as SupportedCountries);
  const computedDefaultCountry = isCountryInAllowedList
    ? country.toLowerCase()
    : (countriesAllowed[0]?.toLowerCase() ?? "us");

  useEffect(() => {
    if (initialCountry) {
      if (initialCountry !== country) {
        setCountry(initialCountry);
      }
      return;
    }

    if (ui.country !== country) {
      setCountry(ui.country);
    }
  }, [initialCountry, ui.country]);

  useEffect(() => {
    // If user typed an explicit international number, infer country from dial code
    let inferredCountry: string | null = null;
    try {
      if (value?.startsWith("+")) {
        const digits = value.replaceAll(/[^\d]/g, "");
        let bestIso: string = "";
        let bestLen = 0;
        defaultCountries
          .filter(
            (c) =>
              allowAllCountries ||
              countriesAllowed.includes((c[1].toUpperCase() as SupportedCountries) || ("" as any))
          )
          .forEach((c) => {
            const dial = String(c[2] || "");
            if (dial && digits.startsWith(dial)) {
              if (dial.length > bestLen) {
                bestIso = String(c[1]);
                bestLen = dial.length;
              }
            }
          });
        if (bestLen > 0) {
          inferredCountry = bestIso.toUpperCase();
          if (inferredCountry !== country) {
            setCountry(inferredCountry);
          }
        }
      }
    } catch {}

    // Validate the phone number directly with awesome-phonenumber.
    // We intentionally do NOT use GetPhoneNumberForDb here because its
    // internal helper (GetPhoneNumberAndExtension) reformats any 10-digit
    // string as a US number, which corrupts international E.164 values
    // (e.g. "+5511987654" becomes "(551) 198-7654" and then re-parses
    // into a wrong E.164, inserting a spurious "55" for Brazilian numbers).
    //
    // We also do NOT call setValue(e164) here. The react-international-phone
    // library already provides the correct E.164 value through its onChange
    // callback, so re-setting it from this effect is both redundant and
    // harmful -- it feeds the country code back into a component configured
    // with disableDialCodeAndPrefix, causing the dial code to appear in
    // the input.
    const normalizeCountry = inferredCountry || country;
    const parsedNumber = parsePhoneNumber(value.trim(), {
      regionCode: normalizeCountry,
    });

    if (!parsedNumber?.valid) {
      setValid(false);
      return;
    }

    setValid(true);
  }, [value, country, setValid, countriesAllowed, allowAllCountries]);

  return (
    <div>
      {label && (
        <label htmlFor={id} className={clsx("block", "text-sm", "font-medium", "mb-1")}>
          {label}
          {required && <span className="ml-1">*</span>}
        </label>
      )}
      <ReactPhoneInput
        name={id ?? "phone"}
        defaultCountry={computedDefaultCountry}
        onChange={(newValue: string, metadata?: any) => {
          try {
            const iso2 = metadata?.country?.iso2 as string | undefined;
            if (iso2) {
              const next = iso2.toUpperCase();
              if (next !== country) {
                setCountry(next);
              }
            }
          } catch {}
          setValue(newValue);
        }}
        value={value}
        countries={defaultCountries
          .filter((country) => {
            return (
              allowAllCountries ||
              countriesAllowed.includes(country[1].toUpperCase() as SupportedCountries)
            );
          })
          .map((country) => {
            if (country[1] === "ar") {
              return [
                "Argentina",
                "ar",
                "54",
                {
                  default: ".. .... ....",
                  "/^9/": ". .. .... ....",
                  "/^8/": "... ... ....",
                  "/^6/": "... ... ....",
                },
                0,
              ] satisfies CountryData;
            } else {
              return country;
            }
          })}
        disabled={disabled}
        disableDialCodeAndPrefix={true}
        placeholder={placeholder}
        onBlur={onBlur}
        inputProps={{ onKeyDown: onKeyDown, onFocus: onFocus, id: id, "aria-label": ariaLabel }}
        style={containerStyle}
        inputStyle={inputStyle}
        className={clsx(
          "rounded-lg",
          "border",
          "[&_button]:!rounded-l-md",
          error ? "border-red-500" : "border-gray-300",
          "[&_button]:!bg-transparent",
          !error && "dark:border-gray-600",
          "dark:[&_button]:!bg-transparent",
          compact ? "[&_button]:!py-2.5" : "[&_button]:!py-5",
          "[&_button]:!pl-2",
          "[&_button]:!pr-1",
          "[&_button]:!border-l-0",
          "[&_button]:!border-t-0",
          "[&_button]:!border-b-0",
          error ? "[&_button]:!border-red-500" : "[&_button]:!border-gray-300",
          !error && "dark:[&_button]:!border-gray-700",
          "dark:[&_ul]:!bg-gray-700",
          "dark:[&_li]:!bg-gray-700",
          "dark:[&_li:hover]:!bg-gray-600",
          className,
          countrySelectorClassName
        )}
        inputClassName={clsx(
          // !important classes beat the inline theme overrides, so they stay
          // only while no themed background/text is supplied.
          !inputStyle?.backgroundColor && "!bg-white",
          !inputStyle?.backgroundColor && "dark:!bg-gray_comp",
          "w-full",
          "!border-none",
          "!rounded-r-lg",
          "!leading-5",
          "!px-3",
          compact ? "!py-2.5" : "!py-5",
          !inputStyle?.color && "!text-800",
          "focus:!ring-gray-300",
          "dark:focus:!ring-gray-700",
          inputClassName
        )}
      />
      {helperText && (
        <p className={clsx("mt-2", "text-xs", error ? "text-red-600" : "text-600")}>{helperText}</p>
      )}
    </div>
  );
};

export default PhoneInput;
